import {
  contractTypeSchema,
  fixedTermContractSchema,
  invoicingMethodOptions,
  nonPrefixedEanSchema,
  noticePeriodSchema,
  productionPointSchema,
  productionPointTypeSchema,
} from "@nano-portal/customers"
import { z } from "zod"
import { dateSchema, fileSchema } from "../utils"
import { objectKeysToZodEnum } from "@nano-portal/shared"
import { Product } from "../../acquisitionForm/form/useAcquisitionFormSchema"
import { useSafeLocalStorage } from "../../../../../utils/localStorage"
import { terminationOptionsSchema } from "../acquisitionForm.utils"
import { useFormContext } from "react-hook-form"

export type ProductionProduct = Exclude<Product, "consumption">

const invoicingMethodSchema = objectKeysToZodEnum(invoicingMethodOptions, {
  errorMap: () => ({ message: "Tato hodnota nesmí být prázdná" }),
})

const productionPointSchemaBase = z.object({
  name: productionPointSchema.shape.name,
  powerKwp: productionPointSchema.shape.powerKwp,

  invoicingMethod: invoicingMethodSchema,

  connectionContract: fileSchema,
  powerStationInfo: z.discriminatedUnion("powerStationType", [
    z.object({
      powerStationType: z.literal(productionPointTypeSchema.Values.NEW),
    }),
    z.object({
      powerStationType: z.literal(productionPointTypeSchema.Values.EXISTING),

      contractInfo: z
        .discriminatedUnion("terminationOption", [
          z.object({
            terminationOption: z.literal(terminationOptionsSchema.Values.DO_NOT_TERMINATE),

            contractEndDate: dateSchema.shape.date,
            contractEndMonth: dateSchema.shape.month,
            contractEndYear: dateSchema.shape.year,

            contractTermination: fileSchema.optional(),
          }),
          z.object({
            terminationOption: z.literal(terminationOptionsSchema.Values.TERMINATE),
            currentSupplier: z.string().min(1).max(100),
            currentContract: z.discriminatedUnion(
              "contractType",
              [
                z.object({
                  contractType: z.literal(contractTypeSchema.Values.VARIABLE_TERM),

                  noticePeriod: noticePeriodSchema,
                }),
                z.object({
                  contractType: z.literal(contractTypeSchema.Values.FIXED_TERM),

                  contractEndDate: dateSchema.shape.date,
                  contractEndMonth: dateSchema.shape.month,
                  contractEndYear: dateSchema.shape.year,
                  terminationNoticeInDaysBeforeEnd: fixedTermContractSchema.shape.terminationNoticeInDaysBeforeEnd,
                }),
              ],
              {
                errorMap: () => ({
                  message: "Tato hodnota nesmí být prázdná",
                }),
              }
            ),
          }),
        ])
        // optional in case of combined contract
        .optional(),
    }),
  ]),
})

const productionPointStandaloneSchema = productionPointSchemaBase.extend({
  ean: nonPrefixedEanSchema,
})

const productionPointCombinedSchema = productionPointSchemaBase.extend({
  ean: nonPrefixedEanSchema.optional(),
})

export function useProductionPointFormSchema<
  P extends ProductionProduct,
  R = P extends "production" ? typeof productionPointStandaloneSchema : typeof productionPointCombinedSchema
>(product: P): R {
  const schema = product === "production" ? productionPointStandaloneSchema : productionPointCombinedSchema

  return schema as R
}

export type ProductionPointStandaloneFormValues = z.infer<typeof productionPointStandaloneSchema>
export type ProductionPointCombinedFormValues = z.infer<typeof productionPointCombinedSchema>
export type ProductionPointFormValues = ProductionPointCombinedFormValues | ProductionPointStandaloneFormValues

export function useStoredProductionPointFormValues(productionTariff: ProductionProduct) {
  const schema = useProductionPointFormSchema(productionTariff)
  return useSafeLocalStorage({ key: "acquisition-form-production-point", schema })
}

export function useProductionPointFormContext() {
  return useFormContext<ProductionPointFormValues>()
}

export type PowerStationInfo = ProductionPointFormValues["powerStationInfo"]

export function getPowerStationContractTermination(powerStationInfo: PowerStationInfo) {
  if (powerStationInfo.powerStationType === "NEW" || powerStationInfo.contractInfo?.terminationOption === "TERMINATE") {
    return
  }

  return powerStationInfo.contractInfo?.contractTermination
}

export function getPowerStationContractEndDate(powerStationInfo: PowerStationInfo) {
  if (powerStationInfo.powerStationType === "NEW" || powerStationInfo.contractInfo?.terminationOption === "TERMINATE") {
    return
  }

  return new Date(
    powerStationInfo.contractInfo?.contractEndYear ?? 0,
    powerStationInfo.contractInfo?.contractEndMonth ?? 0,
    powerStationInfo.contractInfo?.contractEndDate
  )
}
